import { z } from 'zod';
import { toCalendarDate, toTime, toTimeZone } from '@internationalized/date';
import { formatCaseFacilityLocation, formatForDatePicker, formatForTimePicker } from '@features/forms/CaseForms/utils';
import { caseTxCenterFields, reallocationFields } from '@features/forms/CaseForms/schema';
import {
  validateDateInPast,
  validateDateTimeSelection,
  validateTxCenterAddress,
  validateTxCenterCoordinator,
  validateTxCenterId,
} from '@features/forms/CaseForms/validators';
import { Case, NO_COORDINATOR_ID, OrganType, ServiceType } from '@/api/apis/case';
import { createDateTimeFields, getTxCenterEtaZonedDateTime } from '@/utils/helpers';
import { NA_REGION_ID } from '@/constants/serviceRegion.constants';
import { DEFAULT_TIMEZONE } from '@/constants/timezone';

const recipientHospitalDateTimeSchema = z.object(
  createDateTimeFields(
    {
      date: 'recipientHospitalDate',
      time: 'recipientHospitalTime',
      timezone: 'recipientHospitalTimeZone',
    },
    { nullish: true },
  ) as {
    recipientHospitalDate: z.ZodUnion<[z.ZodString, z.ZodNull, z.ZodUndefined]>;
    recipientHospitalTime: z.ZodUnion<[z.ZodString, z.ZodNull, z.ZodUndefined]>;
    recipientHospitalTimeZone: z.ZodUnion<[z.ZodString, z.ZodNull, z.ZodUndefined]>;
  },
);

const txEtaHospitalDateTimeSchema = z.object(
  createDateTimeFields(
    {
      date: 'txEtaHospitalDate',
      time: 'txEtaHospitalTime',
      timezone: 'txEtaHospitalTimeZone',
    },
    { nullish: true },
  ) as {
    txEtaHospitalDate: z.ZodUnion<[z.ZodString, z.ZodNull, z.ZodUndefined]>;
    txEtaHospitalTime: z.ZodUnion<[z.ZodString, z.ZodNull, z.ZodUndefined]>;
    txEtaHospitalTimeZone: z.ZodUnion<[z.ZodString, z.ZodNull, z.ZodUndefined]>;
  },
);

export const caseTxCenterSchema = z.object({
  ...caseTxCenterFields,
  ...reallocationFields,
  organType: z.enum(OrganType),
  ...recipientHospitalDateTimeSchema.shape,
  ...txEtaHospitalDateTimeSchema.shape,
  opoRequest: z.boolean(),
  originalOpoRequest: z.boolean(),
  serviceType: z.enum(ServiceType),
  serviceRegionId: z.string(),
  isExpeditedCase: z.boolean(),
});

export type CaseTxCenterFormType = z.infer<typeof caseTxCenterSchema>;

export const caseTxCenterExtendedSchema = caseTxCenterSchema.superRefine((formData, context) => {
  validateTxCenterId(formData, context, 'txCenterId', 'Tx center should be specified');
  validateTxCenterAddress(formData, context);
  validateTxCenterCoordinator(formData, context);
  validateDateTimeSelection(formData, context, 'recipientHospitalDate', 'recipientHospitalTime');
  validateDateInPast(
    formData,
    context,
    'recipientHospitalDate',
    'recipientHospitalTime',
    'recipientHospitalTimeZone',
    true,
  );
});

export const txCenterDefaultValues = (caseData: Case) => {
  const {
    recipientHospitalDate,
    recipientHospitalTime,
    recipientHospitalTimeZone,
    recipientHospitalORChangeReason,
    txCenterDetails,
    assignedCustomers,
  } = caseData;

  const coordinator = assignedCustomers?.CustomerCoordinators?.find((c) => c.isDefaultTxUser);
  const surgeon = assignedCustomers?.CustomerReceivingSurgeons?.find((c) => c.isDefaultTxUser);
  const location = txCenterDetails?.location
    ? { ...txCenterDetails.location, addressId: txCenterDetails.location.id }
    : undefined;

  return {
    txCenterId: txCenterDetails?.id,
    recipientHospitalDate: formatForDatePicker(recipientHospitalDate),
    recipientHospitalTime: formatForTimePicker(recipientHospitalTime),
    recipientHospitalTimeZone,
    recipientHospitalORChangeReason,
    txCenterCustomLocation: location,
    coordinatorId: coordinator?.customerId ?? '',
    receivingSurgeonId: surgeon?.customerId ?? '',
    isServiceWithoutCoordinator: !assignedCustomers?.CustomerCoordinators?.length,
  };
};

export const txCenterFormValues = (caseData: Case): CaseTxCenterFormType => {
  const {
    txCenterDetails,
    recipientHospitalDate,
    recipientHospitalTime,
    recipientHospitalTimeZone,
    recipientHospitalORChangeReason,
    txEtaHospitalDate,
    txEtaHospitalTime,
    opoRequest,
    assignedCustomers,
    organType,
    reAllocationCaseId,
    serviceRegionId,
    isExpeditedCase,
  } = caseData;
  const { id, name, location, trialPrograms } = txCenterDetails ?? {};

  const coordinator = assignedCustomers?.CustomerCoordinators?.find((c) => c.isDefaultTxUser);
  const surgeon = assignedCustomers?.CustomerReceivingSurgeons?.find((c) => c.isDefaultTxUser);

  const coordinatorId =
    coordinator?.customerId ?? (assignedCustomers?.CustomerCoordinators?.length ? null : NO_COORDINATOR_ID);
  const surgeonId = surgeon?.customerId ?? null;

  const txEtaZonedDateTimeUTC = getTxCenterEtaZonedDateTime(txEtaHospitalDate, txEtaHospitalTime, location?.timezone);
  const txEtaZonedDateTime = txEtaZonedDateTimeUTC ? toTimeZone(txEtaZonedDateTimeUTC, location?.timezone) : null;

  return {
    txCenterId: id,
    txCenterName: name,
    organType,
    txCenterTrialPrograms: trialPrograms,
    txCenterCustomLocation: formatCaseFacilityLocation(location),
    recipientHospitalDate: formatForDatePicker(recipientHospitalDate),
    recipientHospitalTime: formatForTimePicker(recipientHospitalTime),
    recipientHospitalTimeZone: recipientHospitalTimeZone || location?.timezone || DEFAULT_TIMEZONE,
    recipientHospitalORChangeReason,
    txEtaHospitalDate: txEtaZonedDateTime ? toCalendarDate(txEtaZonedDateTime).toString() : null,
    txEtaHospitalTime: txEtaZonedDateTime ? toTime(txEtaZonedDateTime).toString() : null,
    txEtaHospitalTimeZone: location?.timezone || DEFAULT_TIMEZONE,
    opoRequest,
    originalOpoRequest: opoRequest,
    coordinatorId,
    receivingSurgeonId: surgeonId,
    isServiceWithoutCoordinator: coordinatorId === NO_COORDINATOR_ID,
    serviceType: caseData.serviceType,
    reAllocationCase: !!reAllocationCaseId,
    reAllocationCaseId,
    reAllocationCaseTrialProgram: null,
    serviceRegionId: serviceRegionId ?? NA_REGION_ID,
    isExpeditedCase: !!isExpeditedCase,
  };
};
